import React, { useEffect } from "react";

import bgLogin from "@assets/img/arts/bg-login.png";
import d2d from "@assets/img/icons/d2d-new.svg";
import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { SubmittingOverlay } from "@components/Forms/SubmittingOverlay";
import { TextInput } from "@components/Forms/TextInput";
import { Layout } from "@components/Layout";
import { useJwtLogin } from "@hooks/mutation/useJwtLogin";
import { useSearch } from "@hooks/useSearch";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { AuthManager } from "@store/AuthManager";
import { makeMuiStyles, useCommonStyles } from "@styles";
import { FormError } from "@utils/errors";
import {
  formStrings,
  createValidate,
  requiredStringField,
  newEmailField,
} from "@utils/forms";
import { createStrings } from "@utils/local";
import { url } from "@utils/pages";
import clsx from "clsx";
import { Form, Formik } from "formik";
import * as t from "io-ts";
import { observer } from "mobx-react";
import { Link, useHistory } from "react-router-dom";
import * as h from "tyrann-io";

const strings = createStrings({
  ja: {
    title: "【D2D】Membersログイン｜海上輸送サービス",
    forgetPassword: "パスワードをお忘れの方",
    createAccount: "無料アカウントを作成",
    passwordError:
      "パスワードまたはメールアドレスが間違っています。再度入力してください。",
    verifyEmailPrompt:
      "ご登録はまだ完了していません。メールでご本人確認をしてください。",
    emailChangedPrompt:
      "メールアドレスが変更されました。新しいメールアドレス宛に認証用メールが送付されますので、メール内のURLをクリックし認証を完了してください。",
    resendTheMail: "認証メールを再送信する",
    emailVerified: "メール認証に成功しました。ログインしてください。",
    passwordReset: "パスワードのリセットに成功しました。ログインしてください。",
    needCookies: "クッキーを利用しない場合は、ログインできません。",
    unknownError: "エラーが発生しました。",
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.backgroundCenter,
    flex: 1,
    backgroundImage: `url(${bgLogin})`,
    paddingBottom: 130,
  },
  inner: {
    maxWidth: 680,
    margin: "0 auto",
    padding: "0 1rem",
  },
  alertContainer: {
    ...common.flexColumn,
    justifyContent: "center",
    height: 130,
    [theme.breakpoints.down("xs")]: {
      height: 65,
    },
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: "90px 100px",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      padding: "20px",
    },
  },
  logo: {
    display: "block",
    width: 130,
    height: 30,
    alignSelf: "center",
    marginBottom: 50,
  },
  helpContainer: {
    ...common.flexInline,
    justifyContent: "space-between",
  },
  helpLink: {
    ...common.baseButton,
    ...common.middleText,
    ...common.fontWeight600,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  inlineLink: {
    ...common.link,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
}));

const schema = t.type({
  email: newEmailField(formStrings.email),
  password: requiredStringField(formStrings.password),
});

const initialValues: t.TypeOf<typeof schema> = {
  email: "",
  password: "",
};

const query = t.type({
  emailVerified: h.omittable(t.string),
  emailChanged: h.omittable(t.string),
  passwordReset: h.omittable(t.string),
  fromUrl: h.omittable(t.string),
  email: h.omittable(t.string),
});

export const Login: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const mutation = useJwtLogin();

  const authManager = AuthManager.shared;

  const history = useHistory();

  const { search, updateSearch } = useSearch(query);

  useEffect(() => {
    if (
      mutation.error instanceof FormError &&
      mutation.error.message === "NotVerifiedError"
    ) {
      updateSearch({
        emailVerified: "false",
      });
    }
  }, [mutation.error]);

  const renderMutationErrorAlert = () => (
    <Alert
      open={
        mutation.isError &&
        mutation.error instanceof FormError &&
        mutation.error.message !== "NotVerifiedError"
      }
      severity="error"
      onClose={mutation.reset}
      scrollToCenter
      dataId="LoginErrorAlert"
    >
      {mutation.error instanceof FormError
        ? mutation.error.getDisplayMessage() || strings.unknownError
        : (mutation.error as any)?.message}
    </Alert>
  );

  const renderEmailChangedAlert = () => (
    <Alert
      open={search.emailChanged === "true"}
      severity="warning"
      onClose={() => updateSearch({ emailChanged: "" })}
      scrollToCenter
    >
      <div
        className={clsx(
          classes.textGray600,
          classes.smallerText,
          classes.flexInline,
        )}
      >
        {strings.emailChangedPrompt}
        <span>　</span>
        <a
          className={clsx(classes.inlineLink, classes.whitespacePre)}
          onClick={() =>
            history.push(
              `${url("/auth/ResendEmail")}${
                search.email ? "?email=" + encodeURIComponent(search.email) : ""
              }`,
            )
          }
        >
          {strings.resendTheMail}
        </a>
      </div>
    </Alert>
  );

  const renderEmailNotVerifiedAlert = () => (
    <Alert
      open={search.emailVerified === "false"}
      severity="warning"
      onClose={() => updateSearch({ emailVerified: "" })}
      scrollToCenter
    >
      {strings.verifyEmailPrompt}
      <a
        className={classes.inlineLink}
        onClick={() =>
          history.push(
            `${url("/auth/ResendEmail")}${
              search.email ? "?email=" + encodeURIComponent(search.email) : ""
            }`,
          )
        }
      >
        {strings.resendTheMail}
      </a>
    </Alert>
  );

  return (
    <Layout title={strings.title}>
      <div className={classes.container}>
        <div className={classes.inner}>
          <Formik
            validate={createValidate(schema)}
            initialValues={{
              ...initialValues,
              email: search.email ?? "",
            }}
            onSubmit={async (values) => {
              if (authManager.cookieAccepted !== "true") {
                authManager.setCookieDismissed(false);
                return;
              }

              await mutation.mutateAsync(h.unwrap(schema, values));

              if (search.fromUrl) {
                window.location.href = search.fromUrl;
              } else {
                history.push("/utilities/Dashboard");
              }
            }}
          >
            {({ submitCount }) => (
              <>
                <div className={classes.alertContainer}>
                  <SubmittingOverlay />
                  {renderMutationErrorAlert()}
                  {renderEmailNotVerifiedAlert()}
                  {renderEmailChangedAlert()}

                  <Alert
                    open={search.emailVerified === "true"}
                    severity="success"
                    onClose={() => updateSearch({ emailVerified: "" })}
                  >
                    {strings.emailVerified}
                  </Alert>

                  <Alert
                    open={search.passwordReset === "true"}
                    severity="success"
                    onClose={() => updateSearch({ passwordReset: "" })}
                  >
                    {strings.passwordReset}
                  </Alert>
                  <Alert
                    open={
                      submitCount > 0 && authManager.cookieAccepted !== "true"
                    }
                    severity="error"
                    onClose={() => window.location.reload()}
                  >
                    {strings.needCookies}
                  </Alert>
                </div>
                <Form className={classes.formContainer}>
                  <img src={d2d} className={classes.logo} alt="D2D Logo" />

                  <div
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: 4,
                      padding: "16px 20px",
                      color: "#664d03",
                      fontSize: 14,
                      lineHeight: 1.8,
                    }}
                  >
                    <strong>【重要】システム移行のお知らせ</strong>
                    <br />
                    本システムは新システムに移行中です。
                    <br />
                    2026年4月以降にご利用を行われる場合は、
                    <a
                      href="https://d2dasia.com/about-d2d/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#0d6efd", textDecoration: "underline" }}
                    >
                      こちらのフォーム
                    </a>
                    または
                    <a href="mailto:cs@fcstandard.com">顧客窓口</a>
                    よりご連絡ください。
                  </div>

                  <TextInput
                    name="email"
                    autoComplete="current-email"
                    placeholder={formStrings.email}
                    noLabel
                    iconBefore={
                      <MailOutlineIcon
                        style={{
                          paddingRight: 10,
                          width: "1.6rem",
                          height: "1.6rem",
                        }}
                      />
                    }
                  />
                  <TextInput
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder={formStrings.password}
                    noLabel
                    iconBefore={
                      <LockOutlinedIcon
                        style={{
                          paddingRight: 10,
                          width: "1.6rem",
                          height: "1.6rem",
                        }}
                      />
                    }
                  />

                  <div style={{ height: 0 }} />

                  <Button
                    text={formStrings.login}
                    color="primary"
                    type="submit"
                  />

                  <div className={classes.helpContainer}>
                    <Link
                      to="/auth/ForgetPassword"
                      className={classes.helpLink}
                    >
                      {strings.forgetPassword}
                    </Link>
                    <Link to="/auth/CreateAccount" className={classes.helpLink}>
                      {strings.createAccount}
                    </Link>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </div>
    </Layout>
  );
});
